/**
 * @module app.core
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api, ApiQuery } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults, IApiBatchProgress } from 'app/shared/lib/api/api-types';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { EnvironmentService } from 'app/shared/services/environment-service';
import * as Core from 'app/core/models/essette-api';


const defaultConfig: IAssessmentsConfig = {
    serviceRootUrl: ''  // root of "Core" queries. Should not use this one because it lacks proper context for some of its actions
}


@transient()
@autoinject()
export class AssessmentService {

    private isConfigured: boolean = false;
    private config: IAssessmentsConfig;

    constructor(
        protected api: Api,
        private readonly env: EnvironmentService) {
        this.config = defaultConfig;
    }

    public configure(config: IAssessmentsConfig): void {
        this.config = Object.assign({}, defaultConfig, config); // Clones the incoming config object parameter, using defaultConfig as fallback for any missing properties, and assigns it to this.config
        this.isConfigured = true;
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error("Assessment service requires module-specific configuration! Use configure() method.");
        }
    }

    public async get<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.get(query) as IApiQueryResults<T>;
    }

    ////****************** Queries **************************************************////
    public async loadQuestionTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentQuestionType>> {
        const query = this.api
            .query(`assessments/questions/types`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public createAttachmentsByTargetQuery(targetId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], completed: boolean | null = null, fields: string[] = []): ApiQuery {
        this.checkConfiguration();
        const serviceRootUrl = this.config.rollupConfig?.serviceRootUrl || this.config.serviceRootUrl;
        const query = this.api
            .query(`${serviceRootUrl}/${targetId}/assessments/attachments`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(completed !== null) {
            query.filterBoolean('isCompleted', completed);
        }

        if(this.env.application.code === 'Portal.Providers') {
            query.filterBoolean('createdInPortal', true);
        }

        return query;
    }

    public async loadAttachmentsByTarget(targetId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], completed: boolean | null = null): Promise<IApiQueryResults<Core.AssessmentAttachment>> {
        this.checkConfiguration();
        const query = this.createAttachmentsByTargetQuery(targetId, options, includes, completed);

        return await this.api.get(query);
    }

    public async loadAssessments(options?: Partial<IQueryLoadOptions>, includes: string[] = [], externalSystemCode: string | null = null, fields: string[] = []): Promise<IApiQueryResults<Core.Assessment>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(this.config.assessmentsApiFilter) {
            this.config.assessmentsApiFilter(query);
        }

        if(externalSystemCode) {
            query.filterString('externalSystem.code', externalSystemCode, 'eq');
        }

        return await this.api.get(query);
    }

    public async loadAttachmentAnswers(attachmentId?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.AssessmentAnswer>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}/answers`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAttachmentAnswerHistory(attachmentId?: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.AssessmentAnswer>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}/history/answers`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    public async loadAssessment(assessmentId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Assessment>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/${assessmentId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadAttachment(attachmentId: string, includes: string[] = [], inactiveIncludes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentAttachment>> {
        this.checkConfiguration();

        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}`)
            .include(...includes)
            .field(...fields);

        if(inactiveIncludes.length) {
            query.includeWithState('inactive', ...inactiveIncludes);
            if(inactiveIncludes.includes('history.answers')) {
                query.detailTop(this.env.config.pageConfigurations.core.assessments.answerHistoryLimit || 500);
            }
        }

        return await this.api.get(query);
    }

    public async loadAttachmentByTask(taskId: string, includes: string[] = [], attachedToId?: string, fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentAttachment>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/`)
            .filterIdentifier('task.id', taskId)
            .include(...includes)
            .field(...fields);

        if(attachedToId) {
            query.filterIdentifier('attachedTo.id', attachedToId);
        }

        return await this.api.get(query);
    }

    public async loadVersion(versionId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentVersion>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/versions/${versionId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadVersionStructure(versionId: string): Promise<IApiQueryResults<Core.AssessmentVersion>> {
        this.checkConfiguration();
        const includes: string[] = [
            'sections',
            'sections.questions',
            'sections.questions.choices',
            'sections.questions.choices.messages',
            'sections.questions.sharedQuestion'
        ];
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/versions/${versionId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadVersionSectionsWithStructure(versionId: string, fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentSection>> {
        this.checkConfiguration();
        const includes: string[] = [
            'questions',
            'questions.sharedQuestion',
            'questions.choices',
            'questions.choices.sharedChoice',
            'questions.choices.messages'
        ];
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/versions/${versionId}/sections/`)
            .detailTop(100000)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadSectionStructure(sectionId: string): Promise<IApiQueryResults<Core.AssessmentSection>> {
        this.checkConfiguration();
        const includes: string[] = [
            'questions',
            'questions.choices',
            'questions.choices.messages',
            'questions.sharedQuestion'
        ];
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/sections/${sectionId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadSharedQuestionAnswersByMember(memberId: string, sharedQuestionIds: string[] = [], options?: Partial<IQueryLoadOptions>, includes: string[] = [], showHistory: boolean = false): Promise<IApiQueryResults<any>> {
        const query = this.api
            .query('cd/members/assessments/sharedAnswers')
            .include(...includes)
            .applyStandardLoadOptions(options)
            .filterIdentifier('member.id', memberId);

        if(!showHistory) {
            query.filterBoolean('isLatest', true);
        }

        if(sharedQuestionIds.length) {
            query.filterIdentifierArray('sharedQuestion.id', sharedQuestionIds);
        }

        return await this.api.get(query);
    }

    public async loadSession(sessionId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.AssessmentSession>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/sessions/${sessionId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadQuestion(questionId: string, includes: string[] = []): Promise<IApiQueryResults<Core.AssessmentQuestion>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/questions/${questionId}`)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadAnswer(answerId: string, includes: string[] = []): Promise<IApiQueryResults<Core.AssessmentQuestion>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/assessments/attachments/sessions/answers/${answerId}`)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////
    public async insertAttachment(attachment: Core.AssessmentAttachment): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action("insert", `${this.config.serviceRootUrl}/assessments/attachments`, attachment);

        return await this.api.post(action);
    }

    public async insertAttachmentBatch(attachments: Core.AssessmentAttachment[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        this.checkConfiguration();
        const batch = this.api.batch();
        for(const a of attachments) {
            batch.post(this.api.action("insert", `${this.config.serviceRootUrl}/assessments/attachments`, a));
        }
        return await this.api.execute(batch, { progress });
    }

    public async insertAttachmentExternalContent(externalContent: Core.AssessmentAttachmentExternalContent): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action("insert", `${this.config.serviceRootUrl}/assessments/attachments/externalContent`, externalContent);

        return await this.api.post(action);
    }

    public async insertAttachmentExternalContentBatch(externalContents: Core.AssessmentAttachmentExternalContent[]): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const e of externalContents) {
            batch.post(this.api.action('insert', `${this.config.serviceRootUrl}/assessments/attachments/externalContent`, e));
        }
        return await this.api.execute(batch);
    }

    public async updateAttachmentExternalContent(externalContentId: string, externalContent: Core.AssessmentAttachmentExternalContent): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action("update", `${this.config.serviceRootUrl}/assessments/attachments/externalContent/${externalContentId}`, externalContent);

        return await this.api.post(action);
    }

    public async activateAttachment(attachmentId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async completeAttachment(attachmentId: string): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action('complete', `${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}`, { isCompleted: true });

        return await this.api.post(action);
    }

    public async reopenAttachment(attachmentId: string): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action('reopen', `${this.config.serviceRootUrl}/assessments/attachments/${attachmentId}`, { isCompleted: false });

        return await this.api.post(action);
    }

    public async saveSession(session: Core.AssessmentSession): Promise<IApiActionResults> {
        this.checkConfiguration();

        if(this.config.onBeforeSessionSave) {
            await this.config.onBeforeSessionSave(session);
        }

        const actionName = session.id ? 'update' : 'insert';
        const actionUrl = `${this.config.serviceRootUrl}/assessments/attachments/sessions` + (session.id ? `/${session.id}` : '');

        const action = this.api.action(actionName, actionUrl, session);
        return await this.api.post(action);
    }

    public async updateSession(sessionId: string, session: Core.AssessmentSession): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api.action('update', `${this.config.serviceRootUrl}/assessments/attachments/sessions/${sessionId}`, session);

        return await this.api.post(action);
    }

    public async insertAssessment(assessment: Core.Assessment): Promise<IApiActionResults> {
        this.checkConfiguration();

        const action = this.api.action('insert', `${this.config.serviceRootUrl}/assessments`, assessment);
        return await this.api.post(action);
    }

    public async activateAssessment(assessmentId: string, active?: boolean | null): Promise<IApiActionResults> {
        this.checkConfiguration();

        const actionCode = active ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `${this.config.serviceRootUrl}/assessments/${assessmentId}`, { isActive: active });

        if(active) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

}
